<template>
  <b-modal
    ref="modal-cropper"
    modal-class="modal-mat"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
    size="lg"
    @shown="initBox"
    @hidden="finDrag"
  >
    <div class="modal-custom">
      <div class="modal-custom__header">
        <div class="modal-custom__icon modal-custom__icon--primary">
          <i class="fa-solid fa-crop-simple"></i>
        </div>
        <h4 class="modal-custom__title">Recortar imagen</h4>
      </div>

      <div class="modal-custom__body">
        <div class="cropper__ratios">
          <button
            v-for="r in ratios"
            :key="r.label"
            type="button"
            class="cropper__ratio"
            :class="{ 'cropper__ratio--active': aspect === r.value }"
            @click="setAspect(r.value)"
          >{{ r.label }}</button>
        </div>

        <div class="cropper__stage">
          <div class="cropper__wrap">
            <img ref="img" :src="src" class="cropper__img" draggable="false" @load="initBox" />
            <div
              v-if="box.w"
              class="cropper__box"
              :style="boxStyle"
              @mousedown.prevent="iniciarDrag($event, 'move')"
              @touchstart.prevent="iniciarDrag($event, 'move')"
            >
              <span
                v-for="h in handles"
                :key="h"
                :class="'cropper__handle cropper__handle--' + h"
                @mousedown.stop.prevent="iniciarDrag($event, h)"
                @touchstart.stop.prevent="iniciarDrag($event, h)"
              ></span>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-custom__footer">
        <button class="btn-modal btn-modal--ghost" @click="$refs['modal-cropper'].hide()">Cancelar</button>
        <button class="btn-modal btn-modal--primary" :disabled="!box.w" @click="recortar()">
          <i class="fa-solid fa-check"></i> Recortar
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
const MIN_BOX = 20
const MAX_OUTPUT = 1200

const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

export default {
  data() {
    return {
      src: '',
      mime: 'image/jpeg',
      box: { x: 0, y: 0, w: 0, h: 0 },
      drag: null,
      aspect: null,
      handles: ['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se'],
      ratios: [
        { label: 'Libre', value: null },
        { label: '1:1', value: 1 },
        { label: '4:3', value: 4 / 3 },
        { label: '3:4', value: 3 / 4 },
      ],
    }
  },
  computed: {
    boxStyle() {
      return {
        left: `${this.box.x}px`,
        top: `${this.box.y}px`,
        width: `${this.box.w}px`,
        height: `${this.box.h}px`,
      }
    },
  },
  beforeDestroy() {
    this.finDrag()
  },
  methods: {
    // Abre el recortador con una imagen (dataURL)
    open(src, mime) {
      this.box = { x: 0, y: 0, w: 0, h: 0 }
      this.src = src
      if (mime) this.mime = mime
      else this.mime = String(src).startsWith('data:image/png') ? 'image/png' : 'image/jpeg'
      this.$refs['modal-cropper'].show()
    },
    tamanyoImg() {
      const img = this.$refs.img
      return { W: img ? img.clientWidth : 0, H: img ? img.clientHeight : 0 }
    },
    initBox() {
      const { W, H } = this.tamanyoImg()
      if (!W || !H) return
      this.box = { x: W * 0.05, y: H * 0.05, w: W * 0.9, h: H * 0.9 }
      if (this.aspect) this.setAspect(this.aspect)
    },
    setAspect(value) {
      this.aspect = value
      if (!value || !this.box.w) return
      const { W, H } = this.tamanyoImg()
      let w = this.box.w
      let h = w / value
      if (h > H) { h = H; w = h * value }
      if (w > W) { w = W; h = w / value }
      const cx = this.box.x + this.box.w / 2
      const cy = this.box.y + this.box.h / 2
      this.box = {
        x: clamp(cx - w / 2, 0, W - w),
        y: clamp(cy - h / 2, 0, H - h),
        w,
        h,
      }
    },
    punto(e) {
      const t = e.touches ? e.touches[0] : e
      return { x: t.clientX, y: t.clientY }
    },
    iniciarDrag(e, mode) {
      const p = this.punto(e)
      this.drag = { mode, sx: p.x, sy: p.y, box: { ...this.box } }
      window.addEventListener('mousemove', this.moverDrag)
      window.addEventListener('touchmove', this.moverDrag, { passive: false })
      window.addEventListener('mouseup', this.finDrag)
      window.addEventListener('touchend', this.finDrag)
    },
    moverDrag(e) {
      if (!this.drag) return
      e.preventDefault()
      const p = this.punto(e)
      const dx = p.x - this.drag.sx
      const dy = p.y - this.drag.sy
      const { W, H } = this.tamanyoImg()
      const b = this.drag.box
      const mode = this.drag.mode

      if (mode === 'move') {
        this.box = { ...b, x: clamp(b.x + dx, 0, W - b.w), y: clamp(b.y + dy, 0, H - b.h) }
        return
      }

      let left = b.x
      let top = b.y
      let right = b.x + b.w
      let bottom = b.y + b.h

      if (mode.includes('w')) left = clamp(left + dx, 0, right - MIN_BOX)
      if (mode.includes('e')) right = clamp(right + dx, left + MIN_BOX, W)
      if (mode.includes('n')) top = clamp(top + dy, 0, bottom - MIN_BOX)
      if (mode.includes('s')) bottom = clamp(bottom + dy, top + MIN_BOX, H)

      if (this.aspect) {
        let w = right - left
        let h = bottom - top
        if (mode === 'n' || mode === 's') w = h * this.aspect
        else h = w / this.aspect

        if (mode.includes('n')) top = bottom - h
        else bottom = top + h
        if (mode.includes('w')) left = right - w
        else right = left + w

        // Fuera de la imagen: ignorar este movimiento
        if (left < 0 || top < 0 || right > W || bottom > H) return
      }

      this.box = { x: left, y: top, w: right - left, h: bottom - top }
    },
    finDrag() {
      this.drag = null
      window.removeEventListener('mousemove', this.moverDrag)
      window.removeEventListener('touchmove', this.moverDrag)
      window.removeEventListener('mouseup', this.finDrag)
      window.removeEventListener('touchend', this.finDrag)
    },
    recortar() {
      const img = this.$refs.img
      const escalaX = img.naturalWidth / img.clientWidth
      const escalaY = img.naturalHeight / img.clientHeight
      const sw = this.box.w * escalaX
      const sh = this.box.h * escalaY
      const escala = Math.min(1, MAX_OUTPUT / Math.max(sw, sh))

      const canvas = document.createElement('canvas')
      canvas.width = Math.round(sw * escala)
      canvas.height = Math.round(sh * escala)
      const ctx = canvas.getContext('2d')
      if (this.mime !== 'image/png') {
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }
      ctx.drawImage(img, this.box.x * escalaX, this.box.y * escalaY, sw, sh, 0, 0, canvas.width, canvas.height)

      try {
        const dataUrl = this.mime === 'image/png'
          ? canvas.toDataURL('image/png')
          : canvas.toDataURL('image/jpeg', 0.9)
        this.$emit('cropped', dataUrl)
        this.$refs['modal-cropper'].hide()
      } catch (e) {
        this.$emit('error', e)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cropper {
  &__ratios {
    display: flex;
    gap: 6px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  &__ratio {
    padding: 4px 12px;
    border-radius: 6px;
    border: 1px solid #E2E8F0;
    background: transparent;
    color: #64748B;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &--active {
      border-color: #3B82F6;
      background: rgba(#3B82F6, 0.1);
      color: #3B82F6;
    }
  }

  &__stage {
    display: flex;
    justify-content: center;
    background: #0F172A;
    border-radius: 8px;
    padding: 8px;
  }

  &__wrap {
    position: relative;
    overflow: hidden;
    line-height: 0;
    user-select: none;
    touch-action: none;
  }

  &__img {
    display: block;
    max-width: 100%;
    max-height: 60vh;
  }

  &__box {
    position: absolute;
    cursor: move;
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
  }

  &__handle {
    position: absolute;
    width: 12px;
    height: 12px;
    background: #3B82F6;
    border: 2px solid #FFFFFF;
    border-radius: 2px;

    &--n  { top: -6px; left: calc(50% - 6px); cursor: ns-resize; }
    &--s  { bottom: -6px; left: calc(50% - 6px); cursor: ns-resize; }
    &--e  { right: -6px; top: calc(50% - 6px); cursor: ew-resize; }
    &--w  { left: -6px; top: calc(50% - 6px); cursor: ew-resize; }
    &--nw { top: -6px; left: -6px; cursor: nwse-resize; }
    &--ne { top: -6px; right: -6px; cursor: nesw-resize; }
    &--sw { bottom: -6px; left: -6px; cursor: nesw-resize; }
    &--se { bottom: -6px; right: -6px; cursor: nwse-resize; }
  }
}
</style>
